import { isEqual } from "lodash";
import { useEffect, useMemo, useRef } from "react";

function useDeepCompareMemoize<T>(value: T) {
  const ref = useRef<T>(value);
  const signalRef = useRef(0);

  if (!isEqual(value, ref.current)) {
    ref.current = value;
    signalRef.current += 1;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ref.current, [signalRef.current]);
}

type UseEffectParameters = Parameters<typeof useEffect>;
type EffectCallback = UseEffectParameters[0];
type UseEffectDependencies = UseEffectParameters[1];

/**
 * This hook will only run an effect callback if properties of the provided object change, rather
 * than the object reference itself.
 */
export function useDeepCompareEffect(
  callback: EffectCallback,
  dependencies: UseEffectDependencies
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(callback, useDeepCompareMemoize(dependencies));
}
